/**
 * Cross-page storage for the v2 applicant funnel.
 *
 * Why this module exists: the funnel's only anchor is the
 * pre_application_attempt_id issued by POST /public/v1/applicant/pre-apply.
 * Every later step (resume upload URL, audio upload URL, final /apply)
 * is scoped by it, and it is never re-derivable client-side. It used to
 * live in sessionStorage, which dies with the browser tab. Applicants
 * who closed the tab between the orientation video and the form came
 * back to a funnel that could no longer identify them and dead-ended on
 * the resume step with a raw "attempt_id required" string.
 *
 * So funnel identifiers now live in localStorage behind a TTL envelope,
 * with sessionStorage kept as a fallback for two distinct cases:
 *   1. Sessions that were already mid-funnel when this shipped and hold
 *      a bare (un-enveloped) value under the same key — read + migrate.
 *   2. Privacy modes that expose localStorage but throw on access —
 *      degrade to session scope rather than breaking the funnel.
 *
 * The applicant's contact details are promoted too, reluctantly. Keeping
 * them tab-scoped while the attempt id survived produced a worse failure
 * than the one being fixed: the funnel let the applicant back in, showed
 * an empty Personal info step, and the retyped email then failed the
 * backend's email match against the pre-application at the very last
 * step. Exposure is bounded instead by lifetime — everything here is
 * cleared the moment the application is submitted, and expires after
 * APPLICANT_FUNNEL_TTL_MS regardless.
 */

/** Subset of the Web Storage API this module needs. */
export interface FunnelStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface FunnelDeps {
  /** Survives tab close. localStorage in a browser. */
  durable: FunnelStorage | null;
  /** Pre-fix location + privacy-mode fallback. sessionStorage in a browser. */
  legacy: FunnelStorage | null;
  now: number;
  /** Defaults to APPLICANT_FUNNEL_TTL_MS. */
  ttlMs?: number;
}

export const APPLICANT_ATTEMPT_ID_KEY = "glencoco:applicant_attempt_id";
export const APPLICANT_RESUME_S3_KEY = "glencoco:applicant_resume_s3_key";
export const APPLICANT_PREAPP_KEY = "glencoco:applicant_preapp";

/**
 * Every funnel key this module owns. `clearFunnelSession` walks this
 * list, so adding a key here is enough to have it cleaned up on submit.
 */
export const APPLICANT_FUNNEL_KEYS = [
  APPLICANT_ATTEMPT_ID_KEY,
  APPLICANT_RESUME_S3_KEY,
  APPLICANT_PREAPP_KEY,
] as const;

/**
 * How long a half-finished application stays resumable. Seven days is
 * comfortably longer than the observed "left and came back" gap (the
 * repeat-qualifier population clusters inside 24h) while still bounding
 * how long an opaque id sits on a shared machine.
 */
export const APPLICANT_FUNNEL_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const ENVELOPE_VERSION = 1;

/**
 * Upper bound on any single stored value. The largest thing here is the
 * contact blob (four short fields); S3 keys are ~100 chars. Anything an
 * order of magnitude beyond that is not ours.
 */
const MAX_STORED_VALUE_LENGTH = 4096;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface FunnelEnvelope {
  v: number;
  value: string;
  saved_at: number;
}

/**
 * True when the value is shaped like an attempt id the backend will
 * accept. Guards against forwarding leftover junk (a half-written
 * envelope, a value from another app on the same origin) to Glen, which
 * would come back as an opaque "invalid attempt_id".
 */
export function isValidAttemptId(raw: unknown): raw is string {
  return typeof raw === "string" && UUID_RE.test(raw);
}

/**
 * Accepts a legacy bare value that was written before the envelope
 * existed, for keys whose contents are not UUIDs (S3 object keys, the
 * JSON contact blob).
 *
 * This is deliberately permissive. An earlier version validated every
 * legacy value with isValidAttemptId, which silently discarded the
 * resume S3 key ("applicant_resumes/v1/<id>/<ts>/<uuid>.pdf" is neither
 * JSON nor a UUID) for applicants who were mid-funnel at deploy time.
 * The only thing worth rejecting here is a value so malformed it can't
 * be a real one; the typed accessors below apply the per-key rules.
 */
function isPlausibleLegacyValue(raw: string): boolean {
  return raw.length > 0 && raw.length <= MAX_STORED_VALUE_LENGTH;
}

/**
 * Classifies a JSON.parse result.
 *
 * Three outcomes, not two:
 *   - our v1 envelope — decode it;
 *   - a FOREIGN envelope (numeric `v` we don't know, i.e. written by a
 *     newer bundle during a rollback / stale-chunk window) — unusable
 *     here, but it must NOT fall through to the legacy branch: that
 *     would hand back the whole JSON string as if it were an attempt id
 *     or S3 key, and its TTL would never be consulted;
 *   - anything else (the legacy contact blob, random JSON) — the
 *     caller's legacy path decides, using the ORIGINAL string. "JSON
 *     .parse succeeded" is not evidence a value is ours.
 *
 * An unusable timestamp is normalised to NaN so the caller's finite
 * check is the single place expiry is decided.
 */
function classifyParsed(
  parsed: unknown
):
  | { kind: "ours"; envelope: FunnelEnvelope }
  | { kind: "foreign" }
  | { kind: "legacy" } {
  if (typeof parsed !== "object" || parsed === null) {
    return { kind: "legacy" };
  }

  const candidate = parsed as Partial<FunnelEnvelope>;

  if (typeof candidate.v !== "number") {
    return { kind: "legacy" };
  }

  if (candidate.v !== ENVELOPE_VERSION || typeof candidate.value !== "string") {
    return { kind: "foreign" };
  }

  return {
    kind: "ours",
    envelope: {
      v: candidate.v,
      value: candidate.value,
      saved_at:
        typeof candidate.saved_at === "number"
          ? candidate.saved_at
          : Number.NaN,
    },
  };
}

export function encodeFunnelValue(value: string, now: number): string {
  const envelope: FunnelEnvelope = {
    v: ENVELOPE_VERSION,
    value,
    saved_at: now,
  };

  return JSON.stringify(envelope);
}

/**
 * Parses a stored value back into the identifier, or null when it is
 * absent, unreadable or expired.
 *
 * Two deliberate fail-open cases, both chosen because discarding a live
 * application is worse than honouring a stale identifier (a stale id is
 * rejected by the backend, and the form already offers "Restart
 * qualifier" on that path):
 *   - a timestamp that is missing or not a finite number is treated as
 *     fresh rather than expired;
 *   - a timestamp in the future (client clock skew) is not expired.
 */
export function decodeFunnelValue(
  raw: string | null | undefined,
  now: number,
  ttlMs: number
): string | null {
  return decodeDetailed(raw, now, ttlMs).value;
}

/**
 * Full decode result. `evict` is true only when the value is OUR OWN
 * envelope and is spent (expired, or carrying an empty value) — the only
 * two cases where deletion is provably safe. A foreign-version envelope
 * or an unreadable legacy value must be left in place: an older bundle
 * destroying a newer bundle's data (or data it merely failed to parse)
 * is how this module lost applicants' resume keys once already.
 */
function decodeDetailed(
  raw: string | null | undefined,
  now: number,
  ttlMs: number
): { value: string | null; evict: boolean } {
  if (!raw) {
    return { value: null, evict: false };
  }

  let parsed: unknown;

  try {
    parsed = JSON.parse(raw);
  } catch {
    // Not JSON at all: a legacy bare value (attempt id, S3 key).
    return {
      value: isPlausibleLegacyValue(raw) ? raw : null,
      evict: false,
    };
  }

  const classified = classifyParsed(parsed);

  if (classified.kind === "foreign") {
    return { value: null, evict: false };
  }

  if (classified.kind === "legacy") {
    return {
      value: isPlausibleLegacyValue(raw) ? raw : null,
      evict: false,
    };
  }

  const { envelope } = classified;

  if (envelope.value === "") {
    return { value: null, evict: true };
  }

  if (!Number.isFinite(envelope.saved_at)) {
    return { value: envelope.value, evict: false };
  }

  if (now - envelope.saved_at > ttlMs) {
    return { value: null, evict: true };
  }

  return { value: envelope.value, evict: false };
}

function safeGet(storage: FunnelStorage | null, key: string): string | null {
  if (!storage) {
    return null;
  }

  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(
  storage: FunnelStorage | null,
  key: string,
  value: string
): boolean {
  if (!storage) {
    return false;
  }

  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function safeRemove(storage: FunnelStorage | null, key: string): void {
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(key);
  } catch {
    /* storage can reject every operation in hardened privacy modes */
  }
}

/**
 * Resolves the browser storages. Returns nulls during server render and
 * whenever the browser refuses to hand over a storage object at all
 * (some embedded webviews throw on property access, not just on use).
 */
export function browserFunnelDeps(): FunnelDeps {
  if (typeof window === "undefined") {
    return { durable: null, legacy: null, now: Date.now() };
  }

  let durable: FunnelStorage | null = null;
  let legacy: FunnelStorage | null = null;

  try {
    durable = window.localStorage;
  } catch {
    durable = null;
  }

  try {
    legacy = window.sessionStorage;
  } catch {
    legacy = null;
  }

  return { durable, legacy, now: Date.now() };
}

export function readFunnelKey(
  key: string,
  deps: FunnelDeps = browserFunnelDeps()
): string | null {
  const ttlMs = deps.ttlMs ?? APPLICANT_FUNNEL_TTL_MS;

  const durableRaw = safeGet(deps.durable, key);

  if (durableRaw) {
    const decoded = decodeDetailed(durableRaw, deps.now, ttlMs);

    if (decoded.value) {
      return decoded.value;
    }

    // Evict only what is provably ours and spent (our envelope, expired
    // or empty). A foreign-version envelope or a value we merely failed
    // to parse stays put — an older bundle during a rollback must not
    // destroy a newer bundle's data.
    if (decoded.evict) {
      safeRemove(deps.durable, key);
    }
  }

  const legacyRaw = safeGet(deps.legacy, key);

  if (!legacyRaw) {
    return null;
  }

  const value = decodeFunnelValue(legacyRaw, deps.now, ttlMs);

  if (!value) {
    // Deliberately NOT removed. Deleting a value we merely failed to
    // understand is what destroyed in-flight resume keys once already;
    // leaving it costs nothing and keeps the damage from a future
    // parsing mistake recoverable.
    return null;
  }

  // Promote so the value survives the next tab close. Once the durable
  // copy verifiably exists, drop the session-scoped original: we DID
  // understand it (deletion invariant respected), and leaving it behind
  // would let an expired durable value silently resurrect from the
  // timestamp-less legacy copy with a fresh TTL — turning the 7-day
  // bound into "as long as the tab lives".
  //
  // The read-back check is not paranoia: a storage that accepts writes
  // but throws on reads (the asymmetric half of a hardened privacy
  // mode) would otherwise pass the setItem test and we'd delete the
  // only copy we can actually read.
  const encoded = encodeFunnelValue(value, deps.now);

  if (
    safeSet(deps.durable, key, encoded) &&
    safeGet(deps.durable, key) === encoded
  ) {
    safeRemove(deps.legacy, key);
  }

  return value;
}

export function writeFunnelKey(
  key: string,
  value: string,
  deps: FunnelDeps = browserFunnelDeps()
): void {
  const encoded = encodeFunnelValue(value, deps.now);

  if (safeSet(deps.durable, key, encoded)) {
    return;
  }

  // Durable storage refused. A session-scoped copy still carries the
  // applicant through this tab, which is what the funnel did before.
  safeSet(deps.legacy, key, encoded);
}

export function clearFunnelKey(
  key: string,
  deps: FunnelDeps = browserFunnelDeps()
): void {
  safeRemove(deps.durable, key);
  safeRemove(deps.legacy, key);
}

/**
 * Returns the pre-application attempt id, or null when the funnel has no
 * usable session. Callers MUST treat null as "send the applicant back to
 * the qualifier" — every downstream endpoint is scoped by this id.
 */
export function readAttemptId(deps?: FunnelDeps): string | null {
  const value = readFunnelKey(APPLICANT_ATTEMPT_ID_KEY, deps);
  return isValidAttemptId(value) ? value : null;
}

export function writeAttemptId(attemptId: string, deps?: FunnelDeps): void {
  if (!isValidAttemptId(attemptId)) {
    // Refusing to store is right (a malformed id would just fail every
    // later request), but doing it silently is not: the caller's
    // confirm check then fails and the applicant is told their BROWSER
    // is blocking storage. If the backend ever changes the id format,
    // this line is the difference between a one-look diagnosis and a
    // ghost hunt through storage settings.
    console.warn(
      "applicant-session: refusing to store malformed attempt id",
      attemptId
    );
    return;
  }

  writeFunnelKey(APPLICANT_ATTEMPT_ID_KEY, attemptId, deps);
}

export function clearAttemptId(deps?: FunnelDeps): void {
  clearFunnelKey(APPLICANT_ATTEMPT_ID_KEY, deps);
}

/**
 * Confirms that the attempt id now in storage is the one the server just
 * issued, and returns it.
 *
 * The distinction matters because durable storage can still hold an
 * attempt id from an earlier application in the same browser. A bare
 * "is there an id?" check would accept that stale id as proof our write
 * landed and wave the applicant forward under someone else's attempt —
 * the upload-URL endpoints only verify that an attempt exists and
 * passed, not that it belongs to this person.
 */
export function confirmAttemptPersisted(
  returnedAttemptId: string | undefined | null,
  deps?: FunnelDeps
): string | null {
  if (!isValidAttemptId(returnedAttemptId)) {
    return null;
  }

  return readAttemptId(deps) === returnedAttemptId ? returnedAttemptId : null;
}

export function readResumeS3Key(deps?: FunnelDeps): string | null {
  return readFunnelKey(APPLICANT_RESUME_S3_KEY, deps);
}

export function writeResumeS3Key(s3Key: string, deps?: FunnelDeps): void {
  if (!s3Key) {
    return;
  }

  writeFunnelKey(APPLICANT_RESUME_S3_KEY, s3Key, deps);
}

/**
 * The applicant's contact details, stored as the raw JSON string the
 * qualifier produced. Kept opaque here so the shape stays owned by the
 * pages that read it.
 */
export function readPreAppBlob(deps?: FunnelDeps): string | null {
  return readFunnelKey(APPLICANT_PREAPP_KEY, deps);
}

export function writePreAppBlob(blob: string, deps?: FunnelDeps): void {
  if (!blob) {
    return;
  }

  writeFunnelKey(APPLICANT_PREAPP_KEY, blob, deps);
}

/**
 * Drops every funnel key. Called once the application is submitted:
 * without it the attempt id and the applicant's contact details would
 * sit in durable storage for the full TTL, and a later visit to the form
 * would resume an application that is already finished.
 */
export function clearFunnelSession(deps?: FunnelDeps): void {
  const resolved = deps ?? browserFunnelDeps();

  for (const key of APPLICANT_FUNNEL_KEYS) {
    clearFunnelKey(key, resolved);
  }
}
